// Minimal KHQR (Bakong) payload generator — builds a scannable EMVCo QR string for
// an individual Bakong account, with the CRC16 checksum. No network/API needed, so
// no payment data leaves the device. (Bakong auto-verification can be added later;
// for now the seller confirms receipt.)

export interface KhqrInput {
  account: string;        // Bakong account id, e.g. "sothea_chan@aclb"
  merchantName: string;   // <= 25 chars
  city?: string;          // <= 15 chars
  amount?: number;        // omit for a static (no-amount) QR
  currency?: 'USD' | 'KHR';
}

// EMV TLV field: 2-digit id + 2-digit length + value.
const tlv = (id: string, value: string) => id + String(value.length).padStart(2, '0') + value;

// CRC16/CCITT-FALSE (poly 0x1021, init 0xFFFF) over the string, upper-hex, 4 chars.
function crc16(str: string): string {
  let crc = 0xffff;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
      crc &= 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

export function buildKhqr(input: KhqrInput): string {
  const currency = input.currency || 'USD';
  const hasAmount = typeof input.amount === 'number' && input.amount > 0;

  // Merchant account info (tag 29 = individual): sub-tag 00 = Bakong account id.
  const account = tlv('00', input.account);

  let payload = '';
  payload += tlv('00', '01');                         // payload format indicator
  payload += tlv('01', hasAmount ? '12' : '11');      // 12 = dynamic (amount), 11 = static
  payload += tlv('29', account);                      // individual merchant account
  payload += tlv('52', '5999');                       // merchant category code (misc)
  payload += tlv('53', currency === 'USD' ? '840' : '116');
  if (hasAmount) {
    const amt = currency === 'USD' ? input.amount!.toFixed(2) : String(Math.round(input.amount!));
    payload += tlv('54', amt);
  }
  payload += tlv('58', 'KH');                          // country
  payload += tlv('59', (input.merchantName || 'Tinaney').slice(0, 25));
  payload += tlv('60', (input.city || 'Phnom Penh').slice(0, 15));

  // CRC is computed over everything including the CRC tag id+len ("6304").
  payload += '6304';
  return payload + crc16(payload);
}
